import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                          from '../api/api.service';
import { CompanyService }                      from '../companies/company.service';
import { ConfirmService }                      from '../messages/confirm/confirm.service';
import { CurrencyService }                     from '../core/services/currency.service';
import { PurchaseRequestCostModel }            from './typings/uk-postcode.purchase-request.cost';
import { PurchaseRequestQuoteModel }           from './typings/uk-postcode.purchase-request.quote';
import { SessionService }                      from '../sessions/session.service';
import { UKPostcodeAddressesComponent }        from './uk-postcode.addresses.component';
import { UKPostcodeAddressModel }              from './typings/uk-postcode.address';
import { UKPostcodePurchaseRequestsComponent } from './uk-postcode.purchase-requests.component';

@Injectable({
  providedIn: 'root'
})
export class UKPostcodeService {
  public title = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.UK_POSTCODE_LOOKUP.UK_POSTCODE_LOOKUP');

  constructor (
    private apiService      : ApiService,
    private companyService  : CompanyService,
    private confirmService  : ConfirmService,
    private currencyService : CurrencyService,
    private modalService    : NgbModal,
    private sessionService  : SessionService,
    private translateService: TranslateService,
  ) {}

  activate () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
      name: this.title
    }))
    .then(() => this.apiService.get('/company/add_uk_postcode_lookup'))
    .then(() => this.sessionService.refresh());
  }

  confirmPurchase ( quote : PurchaseRequestQuoteModel ) : Promise<any> {
    return this.apiService.post('/chargify/purchase', {
      quantity: quote.quantity,
      service : 'uk_postcode'
    })
    .then(() => this.sessionService.refresh());
  }

  deactivate () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.DEACTIVATE', {
      name: this.title
    }))
    .then(() => this.apiService.get('/company/remove_uk_postcode_lookup'))
    .then(() => this.sessionService.refresh());
  }

  getAvailableRequests () : Promise<number> {
    return Promise.resolve(this.companyService.getUKPostcodeRequests());
  }

  getQuotedPrice ( cost : PurchaseRequestCostModel, quote : PurchaseRequestQuoteModel ) : string {
    return quote.quantity
      ? `${ this.currencyService.getInfo().symbol }${ cost.price * quote.quantity }`
      : null;
  }

  purchase () : Promise<any> {
    return this.apiService.get('/chargify/service_cost', 'uk_postcode')
    .then(( cost : PurchaseRequestCostModel ) => {
      const modal = this.modalService.open(UKPostcodePurchaseRequestsComponent);

      modal.componentInstance.cost = cost;

      return modal.result;
    });
  }

  search ( postcode : string ) : Promise<any> {
    return postcode
      ? this.apiService.get('/uk_postcode/retrieve_uk_postcode_addresses', postcode)
        .then(( addresses : Array<UKPostcodeAddressModel> ) => {
          const modal = this.modalService.open(UKPostcodeAddressesComponent, {
            windowClass: 'lg-modal'
          });

          modal.componentInstance.addresses = addresses;

          return modal.result;
        })
      : Promise.reject(this.translateService.instant('UK_POSTCODE_SPACE.PROVIDE_A_POSTCODE'));
  }
}
